// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {syncVoiceStateToServer} from '@app/features/voice/engine/VoiceChannelConnector';
import {
	createVoiceStateSyncSnapshot,
	shouldApplyServerVoiceState,
	transitionVoiceStateSyncSnapshot,
	type VoiceStateSyncCommand,
	type VoiceStateSyncEvent,
	type VoiceStateSyncPayload,
	type VoiceStateSyncSnapshot,
} from '@app/features/voice/engine/VoiceStateSyncStateMachine';

const logger = new Logger('VoiceStateSyncManager');
const VOICE_STATE_IN_FLIGHT_TIMEOUT_MS = 5000;

export class VoiceStateSyncManager {
	private snapshot: VoiceStateSyncSnapshot = createVoiceStateSyncSnapshot();
	private inFlightTimeout: NodeJS.Timeout | null = null;

	reset(): void {
		this.transition({type: 'sync.reset'});
		logger.debug('Voice state sync cache cleared');
	}

	requestState(payload: VoiceStateSyncPayload): void {
		this.transition({type: 'sync.request', payload});
	}

	confirmServerState(payload: VoiceStateSyncPayload | null): void {
		this.transition({type: 'sync.confirm', payload});
	}

	shouldApplyServerStateToLocalState(payload: VoiceStateSyncPayload): boolean {
		return shouldApplyServerVoiceState(this.snapshot, payload);
	}

	private transition(event: VoiceStateSyncEvent): void {
		this.snapshot = transitionVoiceStateSyncSnapshot(this.snapshot, event);
		this.drainCommands();
	}

	private drainCommands(): void {
		const commands = this.snapshot.context.commands;
		if (commands.length === 0) return;
		this.snapshot = transitionVoiceStateSyncSnapshot(this.snapshot, {type: 'sync.clearCommands'});
		for (const command of commands) {
			this.handleCommand(command);
		}
	}

	private handleCommand(command: VoiceStateSyncCommand): void {
		switch (command.type) {
			case 'scheduleFlush':
				Promise.resolve().then(() => {
					this.transition({type: 'sync.flush'});
				});
				break;
			case 'send':
				logger.debug('Sending voice state update to server', command.payload);
				syncVoiceStateToServer(command.payload.guild_id, command.payload.channel_id, command.payload.connection_id, {
					self_mute: command.payload.self_mute,
					self_deaf: command.payload.self_deaf,
					self_video: command.payload.self_video,
					self_stream: command.payload.self_stream,
					viewer_stream_keys: command.payload.viewer_stream_keys,
				});
				break;
			case 'scheduleInFlightTimeout':
				this.scheduleInFlightTimeout();
				break;
			case 'clearInFlightTimeout':
				this.clearInFlightTimeout();
				break;
		}
	}

	private scheduleInFlightTimeout(): void {
		if (this.inFlightTimeout) return;
		this.inFlightTimeout = setTimeout(() => {
			this.inFlightTimeout = null;
			if (this.snapshot.context.inFlight) {
				logger.warn('Voice state update was not confirmed before timeout; allowing latest pending state to flush', {
					inFlight: this.snapshot.context.inFlight,
					hasPending: Boolean(this.snapshot.context.pending),
				});
			}
			this.transition({type: 'sync.inFlightTimeout'});
		}, VOICE_STATE_IN_FLIGHT_TIMEOUT_MS);
	}

	private clearInFlightTimeout(): void {
		if (!this.inFlightTimeout) return;
		clearTimeout(this.inFlightTimeout);
		this.inFlightTimeout = null;
	}
}
